import {Input} from 'components/Input';
import {Field, FieldProps, Form, Formik} from 'formik';
import React, {memo, ReactNode, useCallback, useRef, useState} from 'react';
import {Arrow} from 'components/icons/Arrow';
import style from './style.module.scss';
import {Checkbox} from 'components/Checkbox';
import classNames from 'classnames';
import {IEmailSubsribe} from 'modules/footer/models';
import {useFooterSubscribeEmailMutation} from 'modules/footer/services';
import * as yup from 'yup';
import {SUBSCRIBE_AGREEMENT_ERROR} from 'modules/footer/constants';
import {Typography} from 'components/Typography';
import {EMAIL_PHRASES} from 'modules/auth/constants';
import Link, {LINK_THEME} from 'components/Link';
import {PERSONAL_DATA_PROCESSING_ROUTE, PRIVACY_POLICY_ROUTE} from 'core/modules/routing/constants';
import Icon from 'components/Icon';
import {EMAIL_REGEXP} from 'core/regex';

interface IProps {
    headerComponent?: ReactNode;
    theme?: 'dark' | 'light';
    showAgreement?: boolean;
    placeholder?: string;
    formName?: 'Article_popup' | 'Main_subscribers_form';
}

const validationSchema = yup.object({
    agreement: yup.boolean().isTrue(SUBSCRIBE_AGREEMENT_ERROR),
    subscription: yup.boolean().isTrue(SUBSCRIBE_AGREEMENT_ERROR),
    email: yup
        .string()
        .trim()
        .required(EMAIL_PHRASES.REQUIRED_EMAIL)
        .email(EMAIL_PHRASES.INCORRECT_EMAIL)
        .matches(EMAIL_REGEXP, {
            message: EMAIL_PHRASES.INCORRECT_EMAIL,
            excludeEmptyString: true,
        }),
});

export const EmailSubscribe = memo<IProps>(function EmailSubscribe({
    headerComponent,
    theme = 'dark',
    showAgreement = false,
    placeholder,
    formName = 'Main_subscribers_form',
}) {
    const inputRef = useRef<HTMLInputElement>(null);

    const [showAgreementState, setShowAgreementState] = useState(showAgreement);

    const [emailSubscribe, {data, reset}] = useFooterSubscribeEmailMutation();

    const handleInputFocus = useCallback(() => {
        setShowAgreementState(true);
    }, []);

    const handleInputBlur = useCallback(() => {
        if (!inputRef.current?.value && !showAgreement) {
            setShowAgreementState(false);
        }
    }, [showAgreement]);

    const handleSubmit = useCallback(
        async (values: IEmailSubsribe) => {
            await emailSubscribe(values);
        },
        [emailSubscribe],
    );

    return (
        <div className={style.subscribe}>
            {headerComponent}
            <Formik
                initialValues={{
                    agreement: false,
                    subscription: true,
                    email: '',
                    formName,
                }}
                onSubmit={handleSubmit}
                validateOnChange={true}
                validationSchema={validationSchema}
            >
                {({submitForm, isValid, values}) => {
                    return (
                        <Form
                            onChange={() => {
                                if (data?.message) {
                                    reset();
                                }
                            }}
                            className={classNames(style.form, style[theme])}
                        >
                            <Field name={'email'}>
                                {({field, form: {errors}}: FieldProps) => {
                                    const errorText = (errors.email ||
                                        errors.agreement ||
                                        errors.subscription) as string;

                                    return (
                                        <Input
                                            {...field}
                                            className={classNames(style.input, 'subscribe-input', {
                                                [style.inputError]: Boolean(errorText),
                                                [style.inputCorrect]: !errorText && Boolean(values.email),
                                            })}
                                            errorText={errorText}
                                            onBlur={handleInputBlur}
                                            onFocus={handleInputFocus}
                                            classNamesMap={{inputLabel: style.inputLabel}}
                                            labelText={placeholder || 'Укажите ваш email'}
                                            ref={inputRef}
                                            onExtraActionClick={submitForm}
                                            trailIcon={
                                                !data?.message ? (
                                                    <Arrow direction={'right'} size={'sm'} />
                                                ) : (
                                                    <Icon icon={'check'} className={style.checkIcon} />
                                                )
                                            }
                                        />
                                    );
                                }}
                            </Field>
                            {data?.message ? (
                                <div className={style.subscribeSuccess}>
                                    <Typography variant={'label-16-m'}>{data?.message}</Typography>
                                </div>
                            ) : (
                                <div
                                    className={classNames(style.agreenmentWrapper, {
                                        [style.showAgreement]: showAgreementState,
                                    })}
                                >
                                    <Field name={'agreement'}>
                                        {({field}: FieldProps) => (
                                            <div
                                                className={classNames(style.agreenment, {
                                                    [style.checkboxShow]: showAgreementState,
                                                })}
                                            >
                                                <Checkbox
                                                    {...field}
                                                    error={!field.value && !isValid}
                                                    checked={field.value}
                                                    className={style.checkbox}
                                                    fontVariant={'label'}
                                                    labelClassname={style.label}
                                                />
                                                <Typography
                                                    className={classNames(style.checkboxText)}
                                                    variant={'label-12-r'}
                                                >
                                                    Я согласен с условиями{' '}
                                                    <Link
                                                        href={PRIVACY_POLICY_ROUTE}
                                                        target={'_blank'}
                                                        theme={LINK_THEME.black}
                                                        className={style.link}
                                                    >
                                                        Политики обработки персональных данных
                                                    </Link>{' '}
                                                    и даю свое{' '}
                                                    <Link
                                                        href={PERSONAL_DATA_PROCESSING_ROUTE}
                                                        target={'_blank'}
                                                        theme={LINK_THEME.black}
                                                        className={style.link}
                                                    >
                                                        согласие на обработку моих персональных данных
                                                    </Link>
                                                </Typography>
                                            </div>
                                        )}
                                    </Field>
                                    <Field name={'subscription'}>
                                        {({field}: FieldProps) => (
                                            <div
                                                className={classNames(style.agreenment, {
                                                    [style.checkboxShow]: showAgreementState,
                                                })}
                                            >
                                                <Checkbox
                                                    error={!field.value && !isValid}
                                                    {...field}
                                                    checked={field.value}
                                                    className={style.checkbox}
                                                    fontVariant={'label'}
                                                    labelClassname={style.label}
                                                />
                                                <Typography
                                                    className={classNames(style.checkboxText)}
                                                    variant={'label-12-r'}
                                                >
                                                    Соглашаюсь получать специальные предложения, новости и рекламную
                                                    информацию через email, sms, push-уведомления и мессенджеры
                                                </Typography>
                                            </div>
                                        )}
                                    </Field>
                                </div>
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
});
